import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import {
  bulkStatus,
  getStats,
  listTickets,
  AdminTicketListItem,
  PaginatedAdminTickets,
  SupportStats,
  TicketStatus,
} from "@/services/supportApi";
import {
  ticketCategoryColor,
  ticketCategoryLabel,
  ticketPriorityColor,
  ticketPriorityLabel,
  ticketStatusColor,
  ticketStatusLabel,
} from "@/lib/supportBadge";

// Valeur sentinelle pour "tous" (Radix Select interdit une value vide).
const ALL = "all";

const STATUS_OPTIONS: TicketStatus[] = ["open", "pending", "on_hold", "solved", "closed"];
const PRIORITY_OPTIONS = ["low", "normal", "high", "urgent"] as const;
const CATEGORY_OPTIONS = [
  "question",
  "billing",
  "bug",
  "feature_request",
  "account",
  "refund",
  "other",
] as const;

function errMsg(e: unknown, fallback = "Erreur"): string {
  return e instanceof Error ? e.message : fallback;
}

function formatDate(iso?: string | null) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatAmount(cents: number, currency: string) {
  return `${(cents / 100).toFixed(2)} ${(currency || "eur").toUpperCase()}`;
}

export default function SupportPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<PaginatedAdminTickets | null>(null);
  const [stats, setStats] = useState<SupportStats | null>(null);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filtres
  const [status, setStatus] = useState<string>(ALL);
  const [category, setCategory] = useState<string>(ALL);
  const [priority, setPriority] = useState<string>(ALL);
  const [assigned, setAssigned] = useState<string>(ALL);
  const [searchInput, setSearchInput] = useState<string>("");
  const [search, setSearch] = useState<string>("");

  // Sélection groupée
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [bulkStatusValue, setBulkStatusValue] = useState<TicketStatus | "">("");
  const [bulkLoading, setBulkLoading] = useState(false);

  // Debounce de la recherche (300ms).
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await listTickets(navigate, {
          page,
          size,
          status: status === ALL ? undefined : status,
          category: category === ALL ? undefined : category,
          priority: priority === ALL ? undefined : priority,
          assigned: assigned === ALL ? undefined : assigned,
          search: search || undefined,
        });
        setData(res);
      } catch (e: unknown) {
        setError(errMsg(e, "Erreur inconnue"));
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, page, size, status, category, priority, assigned, search]);

  // Les stats sont best-effort : elles ne doivent pas bloquer la file.
  useEffect(() => {
    (async () => {
      try {
        const s = await getStats(navigate);
        setStats(s);
      } catch {
        setStats(null);
      }
    })();
  }, [navigate]);

  const pageIds = useMemo(() => new Set((data?.items ?? []).map((t) => t.id)), [data]);
  const selectedCount = useMemo(
    () => Object.entries(selected).filter(([id, v]) => v && pageIds.has(Number(id))).length,
    [selected, pageIds]
  );
  const allSelectedOnPage = useMemo(
    () => (data?.items?.length ?? 0) > 0 && (data?.items ?? []).every((t) => selected[t.id]),
    [data, selected]
  );

  function toggleOne(id: number) {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  function toggleAllOnPage() {
    if (!data?.items?.length) return;
    const next = { ...selected } as Record<number, boolean>;
    const newVal = !allSelectedOnPage;
    for (const t of data.items) next[t.id] = newVal;
    setSelected(next);
  }

  function clearSelection() {
    setSelected((prev) => {
      const copy: Record<number, boolean> = { ...prev };
      for (const id of pageIds) delete copy[id as number];
      return copy;
    });
  }

  async function onBulkApply() {
    if (!bulkStatusValue) return;
    const ids = (data?.items ?? []).filter((t) => selected[t.id]).map((t) => t.id);
    if (!ids.length) return;
    setBulkLoading(true);
    try {
      const res = await bulkStatus(navigate, { ticket_ids: ids, status: bulkStatusValue });
      toast({
        title: "Mise à jour groupée terminée",
        description: `${res.updated} mis à jour, ${res.failed} échec(s).`,
        variant: res.failed > 0 ? "destructive" : "default",
      });
      clearSelection();
      setBulkStatusValue("");
      // Recharge la file pour refléter les nouveaux statuts.
      const refreshed = await listTickets(navigate, {
        page,
        size,
        status: status === ALL ? undefined : status,
        category: category === ALL ? undefined : category,
        priority: priority === ALL ? undefined : priority,
        assigned: assigned === ALL ? undefined : assigned,
        search: search || undefined,
      });
      setData(refreshed);
    } catch (e: unknown) {
      toast({ title: "Échec", description: errMsg(e), variant: "destructive" });
    } finally {
      setBulkLoading(false);
    }
  }

  const canPrev = page > 1;
  const canNext = data ? page < data.pages : false;

  const statusBadge = (s: string) => (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${ticketStatusColor(s)}`}>
      {ticketStatusLabel(s)}
    </span>
  );
  const priorityBadge = (p: string) => (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${ticketPriorityColor(p)}`}>
      {ticketPriorityLabel(p)}
    </span>
  );
  const categoryBadge = (c: string) => (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${ticketCategoryColor(c)}`}>
      {ticketCategoryLabel(c)}
    </span>
  );

  const requesterLabel = (t: AdminTicketListItem) => {
    const id = t.subscription_user_id || t.requester_user_id;
    return id ? `${id.substring(0, 12)}...` : "-";
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Support</h1>
          </div>
        </header>

        <div className="space-y-4 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">File des tickets</h2>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Taille page</label>
              <select
                className="rounded border px-2 py-1 text-sm"
                value={size}
                onChange={(e) => {
                  setPage(1);
                  setSize(parseInt(e.target.value, 10));
                }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Bandeau statistiques (best-effort) */}
          {stats && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded border bg-card p-4">
                <div className="text-xs uppercase text-gray-500">Tickets ouverts</div>
                <div className="text-2xl font-semibold">{stats.open}</div>
              </div>
              <div className="rounded border bg-card p-4">
                <div className="text-xs uppercase text-gray-500">Non assignés</div>
                <div className="text-2xl font-semibold">{stats.unassigned}</div>
              </div>
              <div className="rounded border bg-card p-4">
                <div className="text-xs uppercase text-gray-500">Remboursements en attente</div>
                <div className="text-2xl font-semibold">
                  {stats.refund_pending_count}
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({formatAmount(stats.refund_pending_amount_cents, "eur")})
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Barre de filtres */}
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={status}
              onValueChange={(v) => {
                setPage(1);
                setStatus(v);
              }}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Tous les statuts</SelectItem>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {ticketStatusLabel(s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={category}
              onValueChange={(v) => {
                setPage(1);
                setCategory(v);
              }}
            >
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toutes les catégories</SelectItem>
                {CATEGORY_OPTIONS.map((c) => (
                  <SelectItem key={c} value={c}>
                    {ticketCategoryLabel(c)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={priority}
              onValueChange={(v) => {
                setPage(1);
                setPriority(v);
              }}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Priorité" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toutes les priorités</SelectItem>
                {PRIORITY_OPTIONS.map((p) => (
                  <SelectItem key={p} value={p}>
                    {ticketPriorityLabel(p)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={assigned}
              onValueChange={(v) => {
                setPage(1);
                setAssigned(v);
              }}
            >
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Assignation" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toutes assignations</SelectItem>
                <SelectItem value="unassigned">Non assignés</SelectItem>
              </SelectContent>
            </Select>

            <Input
              className="w-[240px]"
              placeholder="Rechercher (sujet, demandeur)..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          {error && <div className="text-sm text-red-600">{error}</div>}
          {loading && <div className="text-sm text-gray-500">Chargement...</div>}

          <div className="overflow-auto rounded border">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-10 px-3 py-2 text-left">
                    <input
                      type="checkbox"
                      aria-label="Tout sélectionner"
                      checked={allSelectedOnPage}
                      onChange={toggleAllOnPage}
                    />
                  </th>
                  <th className="px-3 py-2 text-left">ID</th>
                  <th className="px-3 py-2 text-left">Sujet</th>
                  <th className="px-3 py-2 text-left">Demandeur</th>
                  <th className="px-3 py-2 text-left">Catégorie</th>
                  <th className="px-3 py-2 text-left">Priorité</th>
                  <th className="px-3 py-2 text-left">Statut</th>
                  <th className="px-3 py-2 text-left">Assigné à</th>
                  <th className="px-3 py-2 text-left">Mis à jour</th>
                </tr>
              </thead>
              <tbody>
                {data?.items?.map((t) => (
                  <tr
                    key={t.id}
                    className="cursor-pointer border-t hover:bg-muted/50"
                    onClick={() => navigate(`/support/${t.id}`)}
                  >
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={!!selected[t.id]}
                        onChange={() => toggleOne(t.id)}
                        aria-label={`Sélectionner le ticket ${t.id}`}
                      />
                    </td>
                    <td className="px-3 py-2">{t.id}</td>
                    <td className="px-3 py-2 font-medium text-blue-600">
                      {t.subject}
                      {t.has_refund && (
                        <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                          Remb.
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{requesterLabel(t)}</td>
                    <td className="px-3 py-2">{categoryBadge(t.category)}</td>
                    <td className="px-3 py-2">{priorityBadge(t.priority)}</td>
                    <td className="px-3 py-2">{statusBadge(t.status)}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {t.assigned_admin_id ? (
                        `${t.assigned_admin_id.substring(0, 12)}...`
                      ) : (
                        <span className="text-gray-400">Non assigné</span>
                      )}
                    </td>
                    <td className="px-3 py-2">{formatDate(t.updated_at)}</td>
                  </tr>
                ))}
                {!data?.items?.length && !loading && (
                  <tr>
                    <td className="px-3 py-4 text-center text-gray-500" colSpan={9}>
                      Aucun ticket
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Barre d'actions groupées (sticky) */}
          {selectedCount > 0 && (
            <div className="sticky bottom-4 left-0 right-0 flex flex-wrap items-center justify-between gap-4 rounded border bg-white px-4 py-3 shadow">
              <div className="text-sm">{selectedCount} sélectionné(s) sur cette page</div>
              <div className="flex items-center gap-2">
                <Select
                  value={bulkStatusValue}
                  onValueChange={(v) => setBulkStatusValue(v as TicketStatus)}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Marquer comme..." />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>
                        {ticketStatusLabel(s)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={clearSelection} disabled={bulkLoading}>
                  Effacer la sélection
                </Button>
                <Button onClick={onBulkApply} disabled={bulkLoading || !bulkStatusValue}>
                  {bulkLoading ? "Application..." : "Appliquer"}
                </Button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {data?.page ?? page} / {data?.pages ?? 1} — {data?.total ?? 0} éléments
            </div>
            <div className="flex gap-2">
              <Button variant="outline" disabled={!canPrev} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Précédent
              </Button>
              <Button variant="outline" disabled={!canNext} onClick={() => setPage((p) => p + 1)}>
                Suivant
              </Button>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
